import {
	INSPIRATION_ALLOWED_CONTENT_TYPES,
	INSPIRATION_LIMITS,
	INSPIRATION_MAX_BYTES,
	inspirationKindForContentType,
	VISUALIZATION_ALLOWED_CONTENT_TYPES,
	VISUALIZATION_LIMITS,
	VISUALIZATION_MAX_BYTES,
	visualizationKindForContentType,
} from "@zplanem/responses"
import type { ProjectMediaCopy, Translations } from "@zplanem/translations"
import { useEffect, useRef, useState } from "react"
import { Button } from "@/components/ui/button"
import { useInspirationMutations } from "../../hooks/use-inspirations"
import { useVisualizationMutations } from "../../hooks/use-visualizations"
import { getTranslations, plural } from "../../i18n"
import { useLocale } from "../../locale-context"
import { ProjectMediaFileRow } from "./project-media-file-row"
import {
	addFiles,
	countByStatus,
	type PickedFile,
	type Rejection,
	type RejectionReason,
	removeFile,
	resetFailed,
	setStatus,
	updateFile,
} from "./project-media-picked-files"

/** The two project media collections this modal serves. */
export type ProjectMediaCollection = "inspiration" | "visualization"

/** How many files one pick may hold. */
const MAX_FILES_PER_BATCH = 10

/**
 * Files uploaded at once. Each file is its own create → Cloudinary → confirm
 * chain, and the server reads its per-project quota outside the write
 * transaction, so a bounded fan-out keeps any overshoot to a rounding error
 * on a 200-item anti-flood cap while still beating a serial loop.
 */
const UPLOAD_CONCURRENCY = 3

interface CollectionConfig {
	accept: string
	kindForContentType: (contentType: string) => string | undefined
	maxBytesForKind: (kind: string) => number
	titleMax: number
	descriptionMax: number
	quota: number
	copy: (t: Translations) => ProjectMediaCopy
}

// Widened once so the config can look a kind up by plain string; the kinds
// themselves always come from the matching `kindForContentType`.
const INSPIRATION_BYTES: Record<string, number> = INSPIRATION_MAX_BYTES
const VISUALIZATION_BYTES: Record<string, number> = VISUALIZATION_MAX_BYTES

const CONFIG: Record<ProjectMediaCollection, CollectionConfig> = {
	inspiration: {
		accept: Object.values(INSPIRATION_ALLOWED_CONTENT_TYPES).flat().join(","),
		kindForContentType: inspirationKindForContentType,
		maxBytesForKind: (kind) => INSPIRATION_BYTES[kind] ?? 0,
		titleMax: INSPIRATION_LIMITS.titleMax,
		descriptionMax: INSPIRATION_LIMITS.descriptionMax,
		quota: INSPIRATION_LIMITS.perProjectMax,
		copy: (t) => t.projects.inspirations,
	},
	visualization: {
		accept: Object.values(VISUALIZATION_ALLOWED_CONTENT_TYPES).flat().join(","),
		kindForContentType: visualizationKindForContentType,
		maxBytesForKind: (kind) => VISUALIZATION_BYTES[kind] ?? 0,
		titleMax: VISUALIZATION_LIMITS.titleMax,
		descriptionMax: VISUALIZATION_LIMITS.descriptionMax,
		quota: VISUALIZATION_LIMITS.perProjectMax,
		copy: (t) => t.projects.visualizations,
	},
}

/**
 * The "＋" affordance opens this: upload one or more files straight to
 * Cloudinary, or add a link. Inspirations and Visualizations behave
 * identically, so they share this modal and differ only by `collection`.
 *
 * On the file tab each picked file carries its own title and description.
 * The link tab is untouched — one URL, one shared title/description pair.
 */
export function ProjectMediaAddModal({
	collection,
	propertyId,
	projectId,
	existingCount,
	onClose,
}: {
	collection: ProjectMediaCollection
	propertyId: string
	projectId: string
	/** Items the project already holds, for the quota pre-check. */
	existingCount: number
	onClose: () => void
}) {
	const { locale } = useLocale()
	const t = getTranslations(locale)
	const config = CONFIG[collection]
	const copy = config.copy(t)
	const uploadCopy = t.projects.mediaUpload

	// Both hooks are pure react-query wiring — no request is made until a
	// mutation runs — so calling both and choosing is cheaper than the
	// conditional hook call the rules of hooks forbid.
	const inspirationMutations = useInspirationMutations(propertyId, projectId)
	const visualizationMutations = useVisualizationMutations(
		propertyId,
		projectId,
	)
	const { uploadFile, addUrl, invalidate } =
		collection === "inspiration" ? inspirationMutations : visualizationMutations

	const [tab, setTab] = useState<"file" | "link">("file")
	const [rows, setRows] = useState<PickedFile[]>([])
	const [rejected, setRejected] = useState<Rejection[]>([])
	const [url, setUrl] = useState("")
	const [title, setTitle] = useState("")
	const [description, setDescription] = useState("")
	const [error, setError] = useState<string | null>(null)
	const [dragging, setDragging] = useState(false)
	const [busy, setBusy] = useState(false)
	// How many files the running batch covers. The progress label must name
	// the batch, not the number of requests currently in the air — that is
	// capped at UPLOAD_CONCURRENCY and drains to zero while the list refetch
	// is still running.
	const [batchSize, setBatchSize] = useState(0)
	const fileRef = useRef<HTMLInputElement>(null)

	// Escape always closes. Uploads are plain `fetch` calls with no timeout,
	// so gating this on `busy` would turn a stalled connection into a trap
	// with no way out but a page reload.
	useEffect(() => {
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") onClose()
		}
		window.addEventListener("keydown", handler)
		return () => window.removeEventListener("keydown", handler)
	}, [onClose])

	const counts = countByStatus(rows)

	function pickFiles(picked: File[]) {
		if (picked.length === 0) return
		setError(null)
		const result = addFiles(rows, picked, {
			kindForContentType: config.kindForContentType,
			maxBytesForKind: config.maxBytesForKind,
			maxFiles: MAX_FILES_PER_BATCH,
			existingCount,
			quota: config.quota,
			titleMax: config.titleMax,
		})
		setRows(result.rows)
		setRejected(result.rejected)
	}

	// Drag-and-drop lives on the dashed buttons rather than a wrapping div:
	// they are already interactive, so the drop zone stays keyboard- and
	// screen-reader-reachable.
	function onDragOver(e: React.DragEvent<HTMLButtonElement>) {
		e.preventDefault()
		setDragging(true)
	}

	function onDragLeave() {
		setDragging(false)
	}

	function onDrop(e: React.DragEvent<HTMLButtonElement>) {
		e.preventDefault()
		setDragging(false)
		if (!busy) pickFiles(Array.from(e.dataTransfer.files ?? []))
	}

	function rejectionMessage(reason: RejectionReason): string {
		switch (reason) {
			case "unsupported":
				return copy.errorUnsupported
			case "tooLarge":
				return copy.errorTooLarge
			case "duplicate":
				return uploadCopy.errorDuplicate
			case "batchLimit":
				return uploadCopy.errorBatchLimit.replace(
					"{max}",
					String(MAX_FILES_PER_BATCH),
				)
			case "quotaFull":
				return uploadCopy.errorQuotaFull.replace("{max}", String(config.quota))
		}
	}

	/** Upload `targets`, at most `UPLOAD_CONCURRENCY` at a time. */
	async function uploadRows(targets: PickedFile[]): Promise<number> {
		let cursor = 0
		let failures = 0
		async function worker(): Promise<void> {
			for (;;) {
				const row = targets[cursor]
				cursor += 1
				if (!row) return
				setRows((current) => setStatus(current, row.id, "uploading"))
				try {
					await uploadFile.mutateAsync({
						file: row.file,
						title: row.title.trim() || undefined,
						description: row.description.trim() || undefined,
					})
					setRows((current) => setStatus(current, row.id, "done"))
				} catch {
					failures += 1
					setRows((current) => setStatus(current, row.id, "failed"))
				}
			}
		}
		await Promise.all(
			Array.from({ length: Math.min(UPLOAD_CONCURRENCY, targets.length) }, () =>
				worker(),
			),
		)
		return failures
	}

	/**
	 * Run a batch and settle the modal.
	 *
	 * The modal closes only when every row in the list has been uploaded —
	 * not merely when this batch had no failures. A batch covers a subset
	 * (`submitFiles` takes the `ready` rows, `retryFailed` the `failed` ones),
	 * so closing on the subset alone would throw away the rows it skipped
	 * along with whatever the user typed into them.
	 *
	 * Rows cannot change while a batch runs — every control that adds or
	 * removes one is disabled by `busy` — so reading the list from this
	 * closure is safe.
	 *
	 * There is deliberately no is-mounted guard around the state updates: on
	 * React 19 an update to an unmounted component is a silent no-op, while a
	 * ref-based guard has to be re-armed in the effect body or StrictMode's
	 * mount/unmount/remount latches it off for good — which silently stops the
	 * modal ever closing.
	 */
	async function runBatch(targets: PickedFile[]) {
		if (targets.length === 0) return
		const untouched = rows.filter(
			(row) =>
				row.status !== "done" &&
				!targets.some((target) => target.id === row.id),
		)
		setError(null)
		setBusy(true)
		setBatchSize(targets.length)
		const failures = await uploadRows(targets)
		// The mutation deliberately does not invalidate per file, so refetch
		// once here. Realtime `*.changed` events may still refetch as each
		// confirm lands; this is about not piling our own on top of those.
		await invalidate()
		setBusy(false)
		if (failures === 0 && untouched.length === 0) {
			onClose()
			return
		}
		if (failures > 0) {
			setError(plural(uploadCopy.filesFailed, failures, locale))
		}
	}

	function submitFiles() {
		setRejected([])
		void runBatch(rows.filter((row) => row.status === "ready"))
	}

	function retryFailed() {
		const targets = rows.filter((row) => row.status === "failed")
		setRows(resetFailed)
		void runBatch(targets)
	}

	async function submitUrl() {
		if (!url.trim()) return
		setError(null)
		setBusy(true)
		try {
			await addUrl.mutateAsync({
				url: url.trim(),
				title: title.trim() || undefined,
				description: description.trim() || undefined,
			})
			onClose()
		} catch {
			setError(copy.errorUpload)
		} finally {
			setBusy(false)
		}
	}

	const submitDisabled =
		busy || (tab === "file" ? counts.ready === 0 : !url.trim())

	const submitLabel =
		tab === "link"
			? busy
				? copy.adding
				: copy.add
			: busy
				? plural(uploadCopy.addingFiles, batchSize, locale)
				: counts.ready === 0
					? // Nothing picked yet, or everything already settled — show the
						// bare verb, as the single-file modal always did.
						copy.add
					: plural(uploadCopy.addFiles, counts.ready, locale)

	return (
		<div
			role="dialog"
			aria-modal="true"
			aria-label={copy.modalTitle}
			className="fixed inset-0 z-50 flex items-center justify-center px-4"
		>
			<button
				type="button"
				aria-label={copy.cancel}
				className="absolute inset-0 bg-black/60"
				onClick={onClose}
			/>
			<div className="relative w-full max-w-lg rounded-lg border border-border bg-popover text-popover-foreground p-6 shadow-2xl">
				<div className="flex items-start justify-between">
					<h2 className="text-xl font-bold text-foreground">
						{copy.modalTitle}
					</h2>
					<button
						type="button"
						onClick={onClose}
						aria-label={copy.cancel}
						className="text-muted-foreground hover:text-foreground px-2 py-1"
					>
						×
					</button>
				</div>

				<div className="mt-4 flex gap-2">
					<TabButton
						active={tab === "file"}
						disabled={busy}
						onClick={() => setTab("file")}
					>
						{copy.tabFile}
					</TabButton>
					<TabButton
						active={tab === "link"}
						disabled={busy}
						onClick={() => setTab("link")}
					>
						{copy.tabLink}
					</TabButton>
				</div>

				<div className="mt-4 space-y-3">
					{tab === "file" ? (
						<div className="space-y-3">
							<input
								ref={fileRef}
								type="file"
								multiple
								accept={config.accept}
								className="hidden"
								onChange={(e) => {
									// Copy the handles before clearing, and always clear, so
									// re-picking a file that was just removed still fires.
									const picked = Array.from(e.currentTarget.files ?? [])
									e.currentTarget.value = ""
									pickFiles(picked)
								}}
							/>

							{rows.length === 0 ? (
								<button
									type="button"
									onClick={() => fileRef.current?.click()}
									onDragOver={onDragOver}
									onDragLeave={onDragLeave}
									onDrop={onDrop}
									className={[
										"w-full cursor-pointer rounded-xl border-2 border-dashed px-4 py-8 flex flex-col items-center justify-center gap-2 text-center transition-colors",
										dragging
											? "border-ring bg-accent"
											: "border-border bg-muted/30 hover:bg-muted/50",
									].join(" ")}
								>
									<UploadIcon />
									<span className="text-sm text-foreground/80">
										{copy.dropHere}
									</span>
									<span className="text-xs text-muted-foreground">
										{copy.fileHint}
									</span>
								</button>
							) : (
								<>
									<p className="text-sm font-medium text-foreground">
										{plural(uploadCopy.filesSelected, rows.length, locale)}
									</p>
									<ul className="max-h-[45vh] space-y-2 overflow-y-auto">
										{rows.map((row) => (
											<ProjectMediaFileRow
												key={row.id}
												row={row}
												copy={copy}
												uploadCopy={uploadCopy}
												titleMax={config.titleMax}
												descriptionMax={config.descriptionMax}
												busy={busy}
												onChange={(patch) =>
													setRows((current) =>
														updateFile(current, row.id, patch),
													)
												}
												onRemove={() =>
													setRows((current) => removeFile(current, row.id))
												}
											/>
										))}
									</ul>
									<button
										type="button"
										disabled={busy || rows.length >= MAX_FILES_PER_BATCH}
										onClick={() => fileRef.current?.click()}
										onDragOver={onDragOver}
										onDragLeave={onDragLeave}
										onDrop={onDrop}
										className={[
											"w-full cursor-pointer rounded-xl border-2 border-dashed px-4 py-3 text-sm text-foreground/80 transition-colors disabled:cursor-not-allowed disabled:opacity-50",
											dragging
												? "border-ring bg-accent"
												: "border-border bg-muted/30 hover:bg-muted/50",
										].join(" ")}
									>
										{uploadCopy.addAnother}
									</button>
								</>
							)}

							{rejected.length > 0 ? (
								<div className="rounded-lg border border-border bg-muted/30 p-3">
									<p className="text-sm font-medium text-foreground">
										{plural(uploadCopy.filesSkipped, rejected.length, locale)}
									</p>
									<ul className="mt-1 space-y-0.5">
										{rejected.map((item) => (
											<li
												key={`${item.name}-${item.reason}`}
												className="text-xs text-muted-foreground"
											>
												<span className="text-foreground/80">{item.name}</span>
												{" — "}
												{rejectionMessage(item.reason)}
											</li>
										))}
									</ul>
								</div>
							) : null}
						</div>
					) : (
						<>
							<div>
								<label
									htmlFor="project-media-url"
									className="block text-sm text-foreground/80 mb-1"
								>
									{copy.urlLabel}
								</label>
								<input
									id="project-media-url"
									type="url"
									value={url}
									onChange={(e) => setUrl(e.target.value)}
									placeholder={copy.urlPlaceholder}
									className="w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-foreground placeholder:text-muted-foreground outline-none focus:border-ring"
								/>
							</div>
							<div>
								<label
									htmlFor="project-media-title"
									className="block text-sm text-foreground/80 mb-1"
								>
									{copy.titleLabel}
								</label>
								<input
									id="project-media-title"
									type="text"
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									maxLength={config.titleMax}
									className="w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-foreground outline-none focus:border-ring"
								/>
							</div>
							<div>
								<label
									htmlFor="project-media-description"
									className="block text-sm text-foreground/80 mb-1"
								>
									{copy.descriptionLabel}
								</label>
								<textarea
									id="project-media-description"
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									maxLength={config.descriptionMax}
									rows={3}
									className="w-full rounded-xl bg-muted border border-border px-4 py-2.5 text-foreground outline-none focus:border-ring resize-none"
								/>
							</div>
						</>
					)}

					{error ? (
						<p role="alert" className="text-sm text-destructive">
							{error}
						</p>
					) : null}
				</div>

				<div className="mt-5 flex justify-end gap-2">
					<Button
						type="button"
						variant="ghost"
						onClick={onClose}
						disabled={busy}
					>
						{copy.cancel}
					</Button>
					{tab === "file" && counts.failed > 0 ? (
						<Button
							type="button"
							variant="outline"
							onClick={retryFailed}
							disabled={busy}
						>
							{uploadCopy.retryFailed}
						</Button>
					) : null}
					<Button
						type="button"
						onClick={tab === "file" ? submitFiles : () => void submitUrl()}
						disabled={submitDisabled}
					>
						{submitLabel}
					</Button>
				</div>
			</div>
		</div>
	)
}

function UploadIcon() {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
			className="w-8 h-8 text-muted-foreground"
			aria-hidden="true"
		>
			<path d="M12 15V3m0 0 4 4m-4-4L8 7" />
			<path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2" />
		</svg>
	)
}

function TabButton({
	active,
	disabled,
	onClick,
	children,
}: {
	active: boolean
	disabled: boolean
	onClick: () => void
	children: React.ReactNode
}) {
	return (
		<button
			type="button"
			onClick={onClick}
			disabled={disabled}
			className={
				active
					? "px-3 py-1.5 rounded-lg text-sm font-medium bg-primary text-primary-foreground disabled:opacity-60"
					: "px-3 py-1.5 rounded-lg text-sm font-medium bg-muted text-muted-foreground hover:text-foreground disabled:opacity-60"
			}
		>
			{children}
		</button>
	)
}
